import { PanelContext } from "../PanelContext";
import { EmptyState } from "../../Display/EmptyState";
import { ControlBar } from "../ControlBar";
import { ControlBarLabelProvider } from "./ControlBarLabelContext";
import { TabStats_ } from "./TabStats";
import { TabHeaderStats_ } from "./TabHeaderStats";
import type { TabProps } from "../Tab";

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function TabPanel_({ tabProps }: { tabProps: TabProps }) {
  const hasLeftSide =
    tabProps.leftFilters || tabProps.leftActions || tabProps.leftStats;
  const hasRightSide =
    tabProps.rightStats ||
    tabProps.rightFilters ||
    tabProps.rightActions ||
    tabProps.menu;
  const hasControlsRow = (hasLeftSide || hasRightSide) && !tabProps.fullBleed;

  // A tab has no in-content title (the title lives in the tab strip), so a
  // description otherwise lands on its own row *below* the right-aligned action
  // row, leaving an empty band above it. When there's no left-hand content,
  // seat the description on the left of that row instead — mirroring
  // Page/Panel headers (description left, actions right).
  const descriptionInControlsRow =
    !!tabProps.description &&
    !tabProps.fullBleed &&
    !hasLeftSide &&
    !!hasRightSide;

  const labelPosition = tabProps.filtersLabelPosition ?? "beside";
  // With above-labels the labels are the anchor, so top-align: every label
  // sits on the same line and controls (including shorter toggles) hang
  // below. Default stays items-center (byte-identical markup for existing tabs).
  const rowAlign = labelPosition === "above" ? "items-start" : "items-center";

  return (
    <div className="mt-4">
      {hasControlsRow && (
        <div
          className={`mb-4 flex flex-wrap ${rowAlign} gap-y-2 ${hasLeftSide || descriptionInControlsRow ? "justify-between" : "justify-end"}`}
        >
          <div className={`flex flex-wrap ${rowAlign} gap-x-5 gap-y-2`}>
            {descriptionInControlsRow && (
              <div className="min-w-0">{tabProps.description}</div>
            )}
            {tabProps.leftFilters && (
              <ControlBarLabelProvider value={labelPosition}>
                <div className={`flex flex-wrap ${rowAlign} gap-x-3 gap-y-2`}>
                  {tabProps.leftFilters}
                </div>
              </ControlBarLabelProvider>
            )}
            {tabProps.leftActions && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
                {tabProps.leftActions}
              </div>
            )}
            {tabProps.leftStats && tabProps.leftStats.length > 0 && (
              <TabHeaderStats_
                items={tabProps.leftStats}
                showDivider={!!(tabProps.leftFilters || tabProps.leftActions)}
              />
            )}
          </div>
          {/* flex-1 + justify-end so the action cluster spans to the right edge
              and the overflow ActionBar has a real width to measure its fold
              against (otherwise a content-sized slot starves it and it folds a
              lone action like "Export" into the kebab with space to spare).
              rightStats ride inside the ControlBar as `leading` so
              [stats | actions | kebab] stay one right-packed cluster. */}
          <div className={`flex min-w-0 flex-1 ${rowAlign} justify-end gap-5`}>
            <ControlBar
              filters={tabProps.rightFilters}
              actions={tabProps.rightActions}
              menu={tabProps.menu}
              actionCollapse={tabProps.actionCollapse ?? "responsive"}
              labelPosition={labelPosition}
              leading={
                tabProps.rightStats && tabProps.rightStats.length > 0 ? (
                  <TabHeaderStats_
                    items={tabProps.rightStats}
                    showDivider={
                      !!(
                        tabProps.rightFilters ||
                        tabProps.rightActions ||
                        tabProps.menu
                      )
                    }
                  />
                ) : undefined
              }
            />
          </div>
        </div>
      )}
      {tabProps.filterBar && !tabProps.fullBleed && (
        <div className="mb-4 border-t border-border pt-3">
          <ControlBar
            filters={tabProps.filterBar}
            labelPosition={labelPosition}
          />
        </div>
      )}
      {tabProps.description &&
        !tabProps.fullBleed &&
        !descriptionInControlsRow && (
          <div className="mb-4">{tabProps.description}</div>
        )}
      {tabProps.statsBar && tabProps.statsBar.length > 0 && (
        <div className={tabProps.fullBleed ? "px-5 pt-4" : "mb-4"}>
          <TabStats_ items={tabProps.statsBar} />
        </div>
      )}
      {tabProps.banners && (
        <div
          className={
            tabProps.fullBleed
              ? "flex flex-col gap-2 px-5 pt-4 pb-2"
              : "mb-4 flex flex-col gap-2"
          }
        >
          {tabProps.banners}
        </div>
      )}
      {tabProps.fullBleed && tabProps.count === 0 ? (
        <div className="p-5">
          <EmptyState
            icon={tabProps.emptyIcon}
            title={`No ${tabProps.title}`}
          />
        </div>
      ) : tabProps.fullBleed ? (
        <PanelContext.Provider value={true}>
          {tabProps.children}
        </PanelContext.Provider>
      ) : (
        tabProps.children
      )}
    </div>
  );
}
